import { getAuth, User } from 'firebase/auth';

import { Logger } from '@mopla/logger';

import { localUserManager } from './localUserManager';

const REFRESH_LISTENER_TIMEOUT_MS = 5000;

/**
 * Get Firebase authentication token with fallback to localUserManager.
 * This function tries multiple strategies to retrieve a valid token:
 * 1. Current Firebase user's token (auto-refreshes if expired)
 * 2. Cached, still-valid token from localStorage (via localUserManager)
 * 3. Refreshed token via onIdTokenChanged listener (bounded by a timeout)
 *
 * On a network/refresh failure we fall back to a still-valid cached token
 * instead of resolving to `undefined`. Sending a request without a bearer
 * produces a 401 that the app treats as a session loss, which force-logs-out
 * the user and wipes local data. See MC-11571.
 */
export const getAuthToken = async (): Promise<string | null | undefined> => {
	const logger = Logger.getInstance('Auth');

	/** TODO stsTokenManager is not documented in the firebase docs. be careful */
	const getValidCachedToken = (): string | undefined => {
		const localUser = localUserManager.get();
		if (
			localUser?.stsTokenManager?.accessToken &&
			localUser.stsTokenManager.expirationTime > Date.now()
		) {
			return localUser.stsTokenManager.accessToken;
		}
		return undefined;
	};

	try {
		const token = await getAuth()?.currentUser?.getIdToken();
		if (token) {
			return token;
		}

		const cachedToken = getValidCachedToken();
		if (cachedToken) {
			return cachedToken;
		}

		// No token yet — wait (bounded) for Firebase to rehydrate the session.
		const updatedUser = await new Promise<User | null>((resolve) => {
			let unsub: () => void = () => undefined;
			const timeoutId = setTimeout(() => {
				unsub();
				resolve(null);
			}, REFRESH_LISTENER_TIMEOUT_MS);
			unsub = getAuth().onIdTokenChanged((user) => {
				clearTimeout(timeoutId);
				resolve(user);
				unsub();
			});
		});

		return await updatedUser?.getIdToken();
	} catch (e) {
		// Network/refresh failure: prefer a still-valid cached token over sending a
		// bearer-less request (which would 401 -> forced logout). See MC-11571.
		const cachedToken = getValidCachedToken();
		if (cachedToken) {
			logger.warn('Token refresh failed; using cached token', {
				online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
			});
			return cachedToken;
		}

		logger.error('Error getting auth token', e);
		return undefined;
	}
};
